// Professional ResetPassword.tsx with background videos
import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../../../helpers/Supabase';
import logo from '../../../assets/vivvy-logo.png';
import '../../../styles/components/Login.scss';

const ResetPassword: React.FC = () => {
  const history = useHistory();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(1);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Background video rotation
  useEffect(() => {
    const videoInterval = setInterval(() => {
      setCurrentVideo(prev => prev === 4 ? 1 : prev + 1);
    }, 8000); // Change video every 8 seconds

    return () => clearInterval(videoInterval);
  }, []);

  // Video transition effect
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.style.opacity = '0';
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.style.opacity = '1';
        }
      }, 500);
    }
  }, [currentVideo]);

  // Floating particles effect
  useEffect(() => {
    const createParticle = () => {
      const particle = document.createElement('div');
      particle.className = 'particle';
      particle.style.left = Math.random() * 100 + '%';
      particle.style.top = Math.random() * 100 + '%';
      particle.style.width = Math.random() * 3 + 1 + 'px';
      particle.style.height = particle.style.width;
      particle.style.animationDelay = Math.random() * 6 + 's';
      particle.style.animationDuration = (Math.random() * 4 + 4) + 's';
      
      const container = document.querySelector('.login-container');
      if (container) {
        container.appendChild(particle);
        
        // Remove particle after animation
        setTimeout(() => {
          if (particle.parentNode) {
            particle.parentNode.removeChild(particle);
          }
        }, 12000);
      }
    };

    // Create particles periodically
    const particleInterval = setInterval(createParticle, 3000);
    
    // Create initial particles
    for (let i = 0; i < 8; i++) {
      setTimeout(createParticle, i * 300);
    }

    return () => clearInterval(particleInterval);
  }, []);

  useEffect(() => {
    // This will auto-load the session when user lands on reset page
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        setError('Session expired or invalid reset link.');
      }
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }

      if (password.length < 8) {
        setError('Password must be at least 8 characters long.');
        return;
      }

      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        setError(error.message);
      } else {
        setSuccess(true);
        setTimeout(() => history.push('/'), 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Background Videos */}
      <div className="background-videos">
        <video
          ref={videoRef}
          className={`background-video ${currentVideo === 1 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../../assets/bg-1.mp4')}
        />
        <video
          className={`background-video ${currentVideo === 2 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../../assets/bg-2.mp4')}
        />
        <video
          className={`background-video ${currentVideo === 3 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../../assets/bg-3.mp4')}
        />
        <video
          className={`background-video ${currentVideo === 4 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../../assets/bg-4.mp4')}
        />
        <div className="video-overlay"></div>
      </div>

      {/* Floating particles container */}
      <div className="floating-particles"></div>
      
      {/* Professional Content */}
      <div className="login-content">
        <div className="brand-section">
          <img src={logo} alt="Vivvy Logo" className="brand-logo" />
          <div className="brand-text">
            <h1>Set New Password</h1>
            <p>Create a secure password for your account</p>
          </div>
        </div>

        <div className="form-section">
          <div className="form-container">
            {success ? (
              <div className="success-section">
                <div className="success-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22,4 12,14.01 9,11.01"/>
                  </svg>
                </div>
                <h2>Password Updated!</h2>
                <p>Your password has been successfully updated.</p>
                <p className="email-note">
                  Redirecting you to the login page...
                </p>
              </div>
            ) : (
              <>
                <div className="form-header">
                  <h2>Create New Password</h2>
                  <p>Enter your new password below</p>
                </div>

                <form className="reset-form" onSubmit={handleSubmit}>
                  <div className="input-group">
                    <label htmlFor="password">New Password</label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your new password"
                      required
                    />
                    <div className="password-strength">
                      <div className={`strength-bar ${password.length >= 8 ? 'strong' : password.length >= 4 ? 'medium' : 'weak'}`}></div>
                      <span className="strength-text">
                        {password.length >= 8 ? 'Strong' : password.length >= 4 ? 'Medium' : 'Weak'}
                      </span>
                    </div>
                  </div>
                  <div className="input-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your new password"
                      required
                    />
                  </div>
                  <button type="submit" className="submit-button" disabled={loading}>
                    {loading ? (
                      <>
                        <div className="loading-spinner"></div>
                        Updating Password...
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </button>
                  {error && <div className="error-message">{error}</div>}
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
